import { router } from "@inertiajs/vue3"
import type { ToastServiceMethods } from "primevue/toastservice"
import { computed, reactive, ref, toRefs } from "vue"

import { apolloClient } from "../queries/client"
import IMPORT_ORDER from "../queries/order/import.gql"

import type {
  ConnectedAgent,
  FetchedDocument,
  FetchedOrderData,
  FetchedOrderParty,
} from "../pages/Order/types"

export type ImportWizardStep =
  | "source"
  | "file-number"
  | "parties"
  | "documents"
  | "review"

const state = reactive<{
  selectedAgent: ConnectedAgent | null
  fileNumber: string
  fetchedOrder: FetchedOrderData | null
  currentStep: ImportWizardStep
  isManual: boolean
  selectedPartyIndices: number[]
  verificationEnabled: boolean
  documentEmailEnabled: boolean
  fetchedDocuments: FetchedDocument[] | null
  selectedDocumentIds: string[]
}>({
  selectedAgent: null,
  fileNumber: "",
  fetchedOrder: null,
  currentStep: "source",
  isManual: false,
  selectedPartyIndices: [],
  verificationEnabled: false,
  documentEmailEnabled: false,
  fetchedDocuments: null,
  selectedDocumentIds: [],
})

const isImporting = ref(false)

export function useImportWizard(toast?: ToastServiceMethods) {
  const reset = () => {
    state.selectedAgent = null
    state.fileNumber = ""
    state.fetchedOrder = null
    state.currentStep = "source"
    state.isManual = false
    state.selectedPartyIndices = []
    state.verificationEnabled = false
    state.documentEmailEnabled = false
    state.fetchedDocuments = null
    state.selectedDocumentIds = []
  }

  const selectAgent = (agent: ConnectedAgent) => {
    state.selectedAgent = agent
    state.isManual = false
    state.currentStep = "file-number"
  }

  const selectManual = () => {
    state.isManual = true
    state.selectedAgent = null
    state.currentStep = "source"
  }

  const setFetchedOrder = (data: FetchedOrderData) => {
    state.fetchedOrder = data
  }

  const toggleParty = (index: number) => {
    const pos = state.selectedPartyIndices.indexOf(index)
    if (pos === -1) {
      state.selectedPartyIndices.push(index)
    } else {
      state.selectedPartyIndices.splice(pos, 1)
    }
  }

  const setVerificationEnabled = (val: boolean) => {
    state.verificationEnabled = val
  }

  const setDocumentEmailEnabled = (val: boolean) => {
    state.documentEmailEnabled = val
  }

  const selectedParties = computed<FetchedOrderParty[]>(() => {
    if (!state.fetchedOrder) return []
    return state.selectedPartyIndices
      .slice()
      .sort((a, b) => a - b)
      .map((i) => state.fetchedOrder!.Parties[i])
      .filter(Boolean)
  })

  const setFetchedDocuments = (docs: FetchedDocument[]) => {
    state.fetchedDocuments = docs
    state.selectedDocumentIds = []
  }

  const toggleDocument = (documentId: string) => {
    const pos = state.selectedDocumentIds.indexOf(documentId)
    if (pos === -1) {
      state.selectedDocumentIds.push(documentId)
    } else {
      state.selectedDocumentIds.splice(pos, 1)
    }
  }

  const docFolderPath = (p: string): string => {
    const normalized = p.replace(/\\/g, "/").replace(/^\/+/, "")
    const parts = normalized.split("/").filter(Boolean)
    return parts.slice(0, -1).join("/")
  }

  const toggleFolder = (folderPath: string) => {
    if (!state.fetchedDocuments) return
    const docsInFolder = state.fetchedDocuments.filter((d) => {
      const folder = docFolderPath(d.folder_path)
      return folder === folderPath || folder.startsWith(folderPath + "/")
    })
    const allSelected = docsInFolder.every((d) =>
      state.selectedDocumentIds.includes(d.document_id),
    )
    if (allSelected) {
      const idsToRemove = new Set(docsInFolder.map((d) => d.document_id))
      state.selectedDocumentIds = state.selectedDocumentIds.filter(
        (id) => !idsToRemove.has(id),
      )
    } else {
      const currentSet = new Set(state.selectedDocumentIds)
      docsInFolder.forEach((d) => currentSet.add(d.document_id))
      state.selectedDocumentIds = [...currentSet]
    }
  }

  const selectAllDocuments = () => {
    if (!state.fetchedDocuments) return
    state.selectedDocumentIds = state.fetchedDocuments.map((d) => d.document_id)
  }

  const deselectAllDocuments = () => {
    state.selectedDocumentIds = []
  }

  const selectedDocuments = computed<FetchedDocument[]>(() => {
    if (!state.fetchedDocuments) return []
    const selectedSet = new Set(state.selectedDocumentIds)
    return state.fetchedDocuments.filter((d) => selectedSet.has(d.document_id))
  })

  const goToStep = (step: ImportWizardStep) => {
    state.currentStep = step
  }

  const resetFromStep = (step: ImportWizardStep) => {
    const steps: ImportWizardStep[] = [
      "source",
      "file-number",
      "parties",
      "documents",
      "review",
    ]
    const idx = steps.indexOf(step)

    if (idx <= 0) {
      state.selectedAgent = null
      state.isManual = false
      state.fileNumber = ""
      state.fetchedOrder = null
      state.selectedPartyIndices = []
      state.verificationEnabled = false
      state.documentEmailEnabled = false
      state.fetchedDocuments = null
      state.selectedDocumentIds = []
    } else if (idx <= 1) {
      state.fileNumber = ""
      state.fetchedOrder = null
      state.selectedPartyIndices = []
      state.verificationEnabled = false
      state.documentEmailEnabled = false
      state.fetchedDocuments = null
      state.selectedDocumentIds = []
    }

    state.currentStep = step
  }

  const stepIndex = computed(() => {
    const map: Record<ImportWizardStep, number> = {
      source: -1,
      "file-number": 0,
      parties: 1,
      documents: 2,
      review: 3,
    }
    return map[state.currentStep]
  })

  const importOrder = async (companyId: string, officerId: string | null) => {
    if (!toast)
      throw new Error("useImportWizard requires toast for importOrder")

    isImporting.value = true
    try {
      const result = await apolloClient.mutate({
        mutation: IMPORT_ORDER,
        variables: {
          companyId,
          agentRegistrationId: state.selectedAgent!.id,
          officerId: officerId || null,
          externalFileNumber: state.fetchedOrder!.FileNumber,
          verificationEnabled: state.verificationEnabled,
          documentEmailEnabled: state.documentEmailEnabled,
          rawSnapshot: state.fetchedOrder,
          parties: selectedParties.value.map((p) => ({
            name: p.FullName,
            role: p.Role,
            externalId: `${p.Role}-${p.Index}`,
            sourceData: p,
          })),
          documents: selectedDocuments.value.map((d) => ({
            documentId: d.document_id,
            fileName: d.file_name,
            folderPath: d.folder_path,
            contentType: d.content_type,
            sizeBytes: d.size_bytes,
          })),
          sourceAttachmentTree: state.fetchedOrder!.Attachments || null,
        },
      })

      if (result?.data?.importOrder?.errors?.length) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: result.data.importOrder.errors.join(", "),
          life: 5000,
        })
        return
      }

      toast.add({
        severity: "success",
        summary: "Success",
        detail: "Order created successfully",
        life: 4000,
      })

      const newOrderGid = result.data.importOrder.order.gid
      router.visit(`/orders/${newOrderGid}`)
    } catch (error) {
      console.error("Import order error:", error)
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "Failed to create order",
        life: 5000,
      })
    } finally {
      isImporting.value = false
    }
  }

  return {
    ...toRefs(state),
    isImporting,
    stepIndex,
    selectedParties,
    selectedDocuments,
    reset,
    selectAgent,
    selectManual,
    setFetchedOrder,
    setFetchedDocuments,
    toggleParty,
    setVerificationEnabled,
    setDocumentEmailEnabled,
    toggleDocument,
    toggleFolder,
    selectAllDocuments,
    deselectAllDocuments,
    goToStep,
    resetFromStep,
    importOrder,
  }
}
